import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { useClickOutside } from '../../internals/useClickOutside';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useId } from '../../internals/useId';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';

import { useNavbarContext } from './NavbarContext';

/* ─────────────────────────── Types ─────────────────────────────────────── */

export type NavDropdownTrigger = 'hover' | 'click' | 'both';

/* ─────────────────────────── NavDropdown ────────────────────────────────── */

export interface NavbarNavDropdownProps {
  /** Text label rendered inside the trigger. */
  label: string;
  /** Whether any child route is currently active (shows the underline). */
  active?: boolean;
  /** How the dropdown opens. @default 'hover' */
  trigger?: NavDropdownTrigger;
  semanticId?: string;
  children: React.ReactNode;
}

const TRANSIENT_TRIGGER = ['$active', '$isOpen'] as const;
const shouldForwardTrigger = (prop: string | number): boolean =>
  !TRANSIENT_TRIGGER.includes(prop as (typeof TRANSIENT_TRIGGER)[number]);

const StyledDropdownWrapper = styled.div.withConfig({
  shouldForwardProp: shouldForwardTrigger,
})<{ $active: boolean; $isOpen: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  height: var(--ui-navbar-h, ${rem(67)});
  padding: 0 ${rem(5)};
  cursor: pointer;

  > button {
    font-weight: 400;
    font-size: ${rem(16)};
    line-height: ${rem(22)};
    color: var(--ui-text, ${palette.Black});
    display: inline-flex;
    align-items: center;
    gap: ${rem(2)};
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    height: 100%;
    position: relative;
    transition: color 0.2s linear;
    font-family: inherit;
    padding: 0;

    &::after {
      content: '';
      height: 3px;
      width: 0;
      background: var(--ui-interactive, ${palette.Purple});
      position: absolute;
      left: 0;
      bottom: 0;
      transition: width 0.2s linear;
    }

    &:hover::after {
      width: 100%;
    }
  }

  ${({ $active, $isOpen }) =>
    ($active || $isOpen) &&
    css`
      > button {
        color: var(--ui-interactive, ${palette.Purple});

        &::after {
          width: 100%;
        }
      }
    `}
`;

const menuSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const menuSlideOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-8px); }
`;

const StyledDropdownPanel = styled.div`
  position: absolute;
  top: calc(var(--ui-navbar-h, ${rem(67)}) + ${rem(6)});
  left: 0;
  z-index: ${zIndex.navbarDropdown};
  background: var(--ui-surface, ${palette.White});
  border-radius: var(--ui-radius-lg, ${rem(11)});
  box-shadow: 0 ${rem(2)} ${rem(20)} var(--ui-shadow-default, rgba(109, 109, 109, 0.15));
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  min-width: ${rem(180)};
  max-width: calc(100vw - ${rem(32)});
  padding: ${rem(10)} ${rem(13)};

  &[data-state='open'] {
    animation: ${menuSlideIn} 200ms ease-out forwards;
  }
  &[data-state='closed'] {
    animation: ${menuSlideOut} 150ms ease-in forwards;
  }
`;

const HOVER_OPEN_DELAY = 80;
const HOVER_CLOSE_DELAY = 200;

function getEnabledItems(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
    (el) => el.dataset.disabled !== 'true',
  );
}

const NavbarNavDropdown: React.FC<NavbarNavDropdownProps> = ({
  label,
  active = false,
  trigger = 'hover',
  semanticId,
  children,
}) => {
  const { openDropdownId, setOpenDropdownId } = useNavbarContext();
  const dropdownId = useId('nav-dropdown');
  const menuId = useId('nav-dropdown-menu');
  const triggerId = useId('nav-dropdown-trigger');

  const isOpen = openDropdownId === dropdownId;
  const setOpen = React.useCallback(
    (open: boolean) => setOpenDropdownId(open ? dropdownId : null),
    [dropdownId, setOpenDropdownId],
  );

  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const hoverOpenTimer = React.useRef<number | undefined>();
  const hoverCloseTimer = React.useRef<number | undefined>();
  const [highlightedIdx, setHighlightedIdx] = React.useState(-1);

  const semanticProps = getSemanticAttributes(semanticId);

  const close = React.useCallback(() => setOpen(false), [setOpen]);

  useClickOutside(panelRef, close, isOpen, { ignoredRefs: [wrapperRef] });
  useEscapeKey(() => {
    close();
    setHighlightedIdx(-1);
    triggerRef.current?.focus();
  }, isOpen);

  const clearTimers = () => {
    if (hoverOpenTimer.current != null) window.clearTimeout(hoverOpenTimer.current);
    if (hoverCloseTimer.current != null) window.clearTimeout(hoverCloseTimer.current);
  };

  React.useEffect(() => clearTimers, []);

  const openIdRef = React.useRef(openDropdownId);
  openIdRef.current = openDropdownId;

  const handleMouseEnter = () => {
    if (trigger !== 'hover' && trigger !== 'both') return;
    clearTimers();
    const delay = openIdRef.current != null ? 0 : HOVER_OPEN_DELAY;
    hoverOpenTimer.current = window.setTimeout(() => {
      setOpenDropdownId(dropdownId);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (trigger !== 'hover' && trigger !== 'both') return;
    clearTimers();
    hoverCloseTimer.current = window.setTimeout(() => {
      if (openIdRef.current === dropdownId) {
        setOpenDropdownId(null);
      }
    }, HOVER_CLOSE_DELAY);
  };

  const handleTriggerClick = () => {
    if (trigger !== 'click' && trigger !== 'both') return;
    setOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const items = getEnabledItems(panelRef.current);
    if (items.length === 0 && !isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        setOpen(true);
        return;
      }
      setHighlightedIdx((prev) => (prev + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIdx((prev) => (prev - 1 + items.length) % items.length);
    } else if (event.key === 'Enter' && isOpen && highlightedIdx >= 0) {
      event.preventDefault();
      items[highlightedIdx]?.click();
    }
  };

  React.useEffect(() => {
    if (!isOpen) return;
    const items = getEnabledItems(panelRef.current);
    items.forEach((el, i) => {
      el.setAttribute('data-highlighted', i === highlightedIdx ? 'true' : 'false');
    });
  }, [highlightedIdx, isOpen]);

  React.useEffect(() => {
    if (!isOpen) setHighlightedIdx(-1);
  }, [isOpen]);

  return (
    <StyledDropdownWrapper
      ref={wrapperRef}
      $active={active}
      $isOpen={isOpen}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      {...semanticProps}
    >
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={handleTriggerClick}
      >
        {label}
        <ChevronIcon $open={isOpen} />
      </button>

      {isOpen && (
        <StyledDropdownPanel
          ref={panelRef}
          id={menuId}
          role="menu"
          aria-labelledby={triggerId}
          tabIndex={-1}
          data-state="open"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </StyledDropdownPanel>
      )}
    </StyledDropdownWrapper>
  );
};

NavbarNavDropdown.displayName = 'Navbar.NavDropdown';

/* ─────────────────────────── ChevronIcon ───────────────────────────────── */

const TRANSIENT_CHEVRON = ['$open'] as const;
const shouldForwardChevron = (prop: string | number): boolean =>
  !TRANSIENT_CHEVRON.includes(prop as (typeof TRANSIENT_CHEVRON)[number]);

const StyledChevron = styled.span.withConfig({
  shouldForwardProp: shouldForwardChevron,
})<{ $open: boolean }>`
  font-size: ${rem(18)};
  line-height: 1;
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: inline-flex;
  align-items: center;
`;

function ChevronIcon({ $open }: { $open: boolean }) {
  return (
    <StyledChevron className="material-symbols-outlined" $open={$open} aria-hidden>
      keyboard_arrow_down
    </StyledChevron>
  );
}

/* ─────────────────────────── NavDropdownItem ────────────────────────────── */

export interface NavbarNavDropdownItemProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  disabled?: boolean;
  semanticId?: string;
}

const TRANSIENT_DD_ITEM = ['$active'] as const;
const shouldForwardDDItem = (prop: string | number): boolean =>
  !TRANSIENT_DD_ITEM.includes(prop as (typeof TRANSIENT_DD_ITEM)[number]);

const StyledDropdownItem = styled.div.withConfig({
  shouldForwardProp: shouldForwardDDItem,
})<{ $active: boolean }>`
  display: flex;
  align-items: center;
  color: var(--ui-text, ${palette.Black});
  cursor: pointer;
  border-bottom: 0.5px solid ${palette.gray[200]};
  white-space: nowrap;

  &:last-child {
    border-bottom: none;
  }

  > a,
  > button,
  > span {
    font-size: ${rem(14)};
    color: var(--ui-text, ${palette.Black});
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-weight: 400;
    border-radius: var(--ui-radius-sm, ${rem(4)});
    padding: ${rem(10)} ${rem(14)};
    transition: all 0.2s linear;
    cursor: pointer;
    width: 100%;
    text-decoration: none;
    background: none;
    border: none;
    font-family: inherit;
    white-space: nowrap;
  }

  &:hover > a,
  &:hover > button,
  &:hover > span,
  &[data-highlighted='true'] > a,
  &[data-highlighted='true'] > button,
  &[data-highlighted='true'] > span {
    background: color-mix(in srgb, var(--ui-interactive, ${palette.Purple}) 10%, transparent);
    color: var(--ui-interactive, ${palette.Purple});
    font-weight: 500;
  }

  ${({ $active }) =>
    $active &&
    css`
      > a,
      > button,
      > span {
        color: var(--ui-interactive, ${palette.Purple});
        font-weight: 500;
      }
    `}

  &[data-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const NavbarNavDropdownItem = React.forwardRef<HTMLDivElement, NavbarNavDropdownItemProps>(
  function NavbarNavDropdownItem(
    { active = false, disabled = false, semanticId, children, onClick, ...rest },
    ref,
  ) {
    const itemId = useId('nav-dropdown-item');
    const { setOpenDropdownId } = useNavbarContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      onClick?.(e);
      setOpenDropdownId(null);
    };

    return (
      <StyledDropdownItem
        ref={ref}
        id={itemId}
        role="menuitem"
        tabIndex={-1}
        $active={active}
        data-active={active || undefined}
        data-disabled={disabled || undefined}
        onClick={handleClick}
        {...semanticProps}
        {...rest}
      >
        {children}
      </StyledDropdownItem>
    );
  },
);

NavbarNavDropdownItem.displayName = 'Navbar.NavDropdownItem';

/* ─────────────────────────── Exports ───────────────────────────────────── */

export { NavbarNavDropdown, NavbarNavDropdownItem };
